/**
 * Ask setup — Trodo's models, or one of your own.
 *
 * OFF: Ask runs on Trodo's models against the credit balance.
 * ON:  Ask runs on ONE model the team chose, on its own key, for every step —
 *      intake, planning, investigators, the report, memory. Nothing sits
 *      behind it: no fallback to another key, none to Trodo's.
 *
 * One model, not two. The old Heavy/Base pair asked a customer to reason about
 * our internal roles; which step needs how much thinking is the harness's job.
 * The server still stores the one model in both engine roles, because that is
 * what every engine, span and ledger row reads.
 *
 * Scope is the TEAM. This used to write the org's credit switch, which turned
 * Trodo's models off for sibling projects and — because turning it off left
 * the slots in place — kept a team on its own key after it chose Trodo.
 *
 * Any connected model can be chosen; the harness supplies tool calling and
 * structured output where a model has none. The list leads with the models we
 * recommend for answer quality, and Save runs a real check — a two-turn tool
 * loop and a structured reply — before the choice is accepted.
 *
 * Embeddings are not here and never will be: always Trodo's, never charged.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { listIntegrations } from 'features/evals/api/evalsApi';
import {
  getAskConfig, saveAskConfig, probeAskSlot,
} from 'features/settings/api/askConfigApi';
import { useTeam } from 'shared/context/teamContext';
import { usePermissions } from 'shared/hooks/usePermissions';
import { PERMISSIONS } from 'shared/constants/permissions';
import { useCatalogModels } from 'shared/hooks/useCatalogModels';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import ProviderLogo, { providerLabel } from 'shared/components/ui/ProviderLogo';
import { groupAskModels } from 'shared/constants/recommendedAskModels';
import {
  settingsCardShell, settingsPrimaryButtonClass, settingsSecondaryButtonClass,
} from 'features/settings/components/settingsTheme';

const INK = 'var(--text-primary)';
const MUTED = 'var(--text-muted)';

const MODES = [
  { value: 'quick', label: 'Quick' },
  { value: 'deep', label: 'Deep research' },
];

const EFFORTS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'max', label: 'Max' },
];

/** A plain switch. The app has no shared one, and MUI's carries its own theme. */
function Toggle({ checked, disabled, onChange, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: 34,
        height: 20,
        borderRadius: 999,
        padding: 2,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: checked ? 'flex-end' : 'flex-start',
        background: checked ? 'var(--brand-primary, #f97316)' : 'var(--surface-hover)',
        border: '1px solid var(--border-default)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 120ms ease',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: checked ? '#fff' : 'var(--text-muted)',
          display: 'block',
        }}
      />
    </button>
  );
}

export default function AskSetupPanel() {
  const { selectedTeam } = useTeam();
  const teamId = selectedTeam?.id;
  const { can } = usePermissions();
  const canManage = can?.(PERMISSIONS.INTEGRATIONS_DEVELOPER_MANAGE);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [integrations, setIntegrations] = useState([]);
  // { integration_id, model } — the one model, when the team brings its own.
  const [model, setModel] = useState({});
  const [ownModels, setOwnModels] = useState(false);
  const [defaults, setDefaults] = useState({ default_mode: 'quick', default_effort: 'medium' });
  const [probe, setProbe] = useState(null);
  // What the server last gave us. Save and Reset appear only when the form
  // differs from this — a Save button that is always there says nothing about
  // whether there is anything to save.
  const [saved, setSaved] = useState(null);

  const modelsForCredential = useCatalogModels(teamId, integrations);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      const [creds, cfg] = await Promise.all([
        listIntegrations(teamId),
        getAskConfig(teamId),
      ]);
      const loadedOwn = cfg?.own_models === true;
      // Only an own model the server honours is shown as chosen; a legacy
      // Heavy/Base pair of different models reads as nothing chosen yet.
      const lead = cfg?.config?.slots?.lead;
      const loadedModel = loadedOwn && lead ? { integration_id: lead.integration_id, model: lead.model } : {};
      const loadedDefaults = {
        // A stored 'auto' from before the classifier was deleted reads as quick,
        // which is what the server makes of it too.
        default_mode: cfg?.config?.default_mode === 'deep' ? 'deep' : 'quick',
        default_effort: cfg?.config?.default_effort || 'medium',
      };
      setIntegrations(creds || []);
      setModel(loadedModel);
      setOwnModels(loadedOwn);
      setDefaults(loadedDefaults);
      setSaved({ model: loadedModel, ownModels: loadedOwn, defaults: loadedDefaults });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not load Lucid setup');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const credById = useMemo(
    () => Object.fromEntries((integrations || []).map((i) => [String(i.id), i])),
    [integrations],
  );

  /**
   * The user's own LABEL is the name; the key prefix is only a tiebreak. It
   * was the other way round, which made four Azure deployments on one key
   * render as four identical "Azure OpenAI ·····y0hW" rows — the prefix is
   * per-KEY, and one key can front any number of deployments.
   *
   * No "Trodo" entry: that choice is the switch above, not a per-role option.
   */
  const providerOptions = useMemo(() => (integrations || []).map((i) => ({
    value: String(i.id),
    label: i.label || providerLabel(i.provider),
    hint: i.label ? providerLabel(i.provider) : (i.key_prefix || ''),
    icon: <ProviderLogo provider={i.provider} size={16} />,
  })), [integrations]);

  const chooseModel = (patch) => {
    setNotice(null);
    setProbe(null);
    setModel((prev) => (patch === null ? {} : { ...prev, ...patch }));
  };

  const chosen = Boolean(model?.integration_id && model?.model);

  /**
   * The picker appears BECAUSE the switch is on — that is the order the page
   * reads in — and a missing model is caught at save, where it can say so
   * instead of silently undoing the click.
   */
  const dirty = useMemo(() => {
    if (!saved) return false;
    return JSON.stringify({ model, ownModels, defaults })
      !== JSON.stringify({ model: saved.model, ownModels: saved.ownModels, defaults: saved.defaults });
  }, [model, ownModels, defaults, saved]);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      // Check only what we are about to rely on: a team staying on Trodo's
      // models has nothing to check.
      if (ownModels && !chosen) {
        setError('Choose a model before switching Lucid to your own model.');
        setSaving(false);
        return;
      }
      if (ownModels) {
        const result = await probeAskSlot(teamId, {
          integrationId: model.integration_id, model: model.model, role: 'lead',
        });
        setProbe(result);
        if (result && result.ok === false) {
          setError(result.error || 'This model failed its check.');
          setSaving(false);
          return;
        }
      }

      const one = ownModels ? { integration_id: model.integration_id, model: model.model } : null;
      await saveAskConfig(teamId, {
        ...(one ? { slots: { lead: one, worker: one } } : {}),
        own_models: ownModels,
        ...defaults,
      });
      setSaved({ model: one || {}, ownModels, defaults });
      if (!ownModels) setModel({});
      setNotice('Saved');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!teamId) return null;

  if (loading) {
    return (
      <div className={`${settingsCardShell} p-3.5`}>
        <div className="flex items-center gap-2 text-sm" style={{ color: MUTED }}>
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  const noCredentials = (integrations || []).length === 0;
  const modelsHref = `/${encodeURIComponent(selectedTeam?.name || '')}/settings/models`;
  // Turning it on is now allowed as soon as there is a key to point at; the
  // models to point it at appear underneath once it is on.
  const canEnable = canManage && !noCredentials;

  return (
    <div className="space-y-4">
      <div className={`${settingsCardShell} p-3.5`}>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[13px]" style={{ color: INK }}>Use your own models</div>
            <div className="text-[11px] mt-0.5" style={{ color: MUTED }}>
              {ownModels
                ? 'Every step of Lucid runs on the model you choose, on your key.'
                : 'Lucid runs on Trodo’s models and your credit balance.'}
            </div>
          </div>
          <Toggle
            checked={ownModels}
            disabled={!canEnable}
            onChange={(v) => { setNotice(null); setOwnModels(v); }}
            ariaLabel="Use your own models"
          />
        </div>

        {noCredentials ? (
          <div className="text-[11px] mt-2" style={{ color: MUTED }}>
            No model keys yet — <Link to={modelsHref} className="underline" style={{ color: INK }}>add one</Link>.
          </div>
        ) : null}
      </div>

      {/* Only when you are actually bringing your own. On Trodo's models these
          pickers configure nothing, and showing them implied a choice that was
          not being made. */}
      {ownModels ? (() => {
        const cred = model.integration_id ? credById[String(model.integration_id)] : null;
        const modelOptions = cred ? groupAskModels(modelsForCredential(cred) || []) : [];
        return (
          <div className={`${settingsCardShell} p-3.5`}>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[13px]" style={{ color: INK }}>Model</div>
                <div className="text-[11px] mt-0.5" style={{ color: MUTED }}>
                  Used for every step. Recommended models give the best answers.
                </div>
                {probe && !probe.ok ? (
                  <div className="text-[11px] mt-1 flex items-start gap-1" style={{ color: 'var(--danger, #f87171)' }}>
                    <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                    <span>{probe.error}</span>
                  </div>
                ) : null}
                {probe && probe.ok && probe.note ? (
                  <div className="text-[11px] mt-1" style={{ color: MUTED }}>{probe.note}</div>
                ) : null}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <BrandDropdown
                  value={model.integration_id ? String(model.integration_id) : ''}
                  onChange={(v) => (v
                    ? chooseModel({ integration_id: parseInt(v, 10), model: '' })
                    : chooseModel(null))}
                  options={providerOptions}
                  placeholder="Provider"
                  disabled={!canManage || noCredentials}
                  compact
                  menuMinWidth={220}
                  ariaLabel="Model provider"
                />
                <BrandDropdown
                  value={model.model || ''}
                  onChange={(v) => chooseModel({ model: v })}
                  options={modelOptions}
                  placeholder="Model"
                  disabled={!canManage || !model.integration_id}
                  compact
                  menuMinWidth={280}
                  ariaLabel="Model"
                />
              </div>
            </div>
          </div>
        );
      })() : null}

      <div className={`${settingsCardShell} p-3.5`}>
        <h3 className="text-[13px] font-[450] tracking-tight mb-4" style={{ color: INK }}>Defaults</h3>
        <div className="divide-y divide-[color:var(--border-subtle)]">
          <div className="flex items-center justify-between gap-4 py-3 first:pt-0">
            <span className="text-[13px]" style={{ color: INK }}>Mode</span>
            <BrandDropdown
              value={defaults.default_mode}
              onChange={(v) => setDefaults((d) => ({ ...d, default_mode: v }))}
              options={MODES}
              disabled={!canManage}
              compact
              ariaLabel="Default mode"
            />
          </div>
          <div className="flex items-center justify-between gap-4 py-3 last:pb-0">
            <span className="text-[13px]" style={{ color: INK }}>Research depth</span>
            <BrandDropdown
              value={defaults.default_effort}
              onChange={(v) => setDefaults((d) => ({ ...d, default_effort: v }))}
              options={EFFORTS}
              disabled={!canManage}
              compact
              ariaLabel="Default research depth"
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="text-[11px]" style={{ color: 'var(--danger, #f87171)' }}>{error}</div>
      ) : null}

      {/* Save and Reset exist only while there is something to save or reset.
          A permanently present Save says nothing about whether the page holds
          unsaved work; appearing on the first edit does. `notice` outlives it
          so "Saved" is still readable after the buttons go. */}
      {canManage && dirty ? (
        <div className="flex items-center gap-2">
          <button type="button" className={settingsPrimaryButtonClass} onClick={onSave} disabled={saving}>
            {saving ? 'Checking…' : 'Save'}
          </button>
          <button type="button" className={settingsSecondaryButtonClass} onClick={load} disabled={saving}>
            Reset
          </button>
        </div>
      ) : null}
      {notice ? <span className="text-[11px]" style={{ color: MUTED }}>{notice}</span> : null}
    </div>
  );
}
